"use client";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { X, Trash2 } from "lucide-react";
import { postWithAuth } from "@/lib/api-helpers";
import { formatInstant } from "@/lib/timezone";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
}


interface ChatInterfaceProps {
  currentNoteId?: string | null;
  currentNoteTitle?: string;
  userEmail?: string;
  userName?: string;
  activeWorkspaceId?: string | null;
  onThinkingChange?: (thinking: boolean) => void;
  newChatVersion?: number;
  showHistory?: boolean;
  onHistoryClose?: () => void;
}

const QUICK_ACTIONS = [
  { label: "Search notes", prompt: "Search my notes" },
  { label: "Create a note", prompt: "Create a new note" },
  { label: "Summarize this page", prompt: "Summarize this page" },
  { label: "Write something", prompt: "Write something for me" },
];

const MAX_SESSIONS = 10;
const MAX_MESSAGES_PER_SESSION = 50;

function sessionsKey(email: string) {
  return `chatSessions_${email}`;
}

function legacyKey(email: string) {
  return `chatWidgetHistory_${email}`;
}

function loadAllSessions(email: string): ChatSession[] {
  if (typeof window === "undefined") return [];
  try {
    const saved = localStorage.getItem(sessionsKey(email));
    if (saved) return JSON.parse(saved) as ChatSession[];

    // One-time migration from old single-session format
    const legacy = localStorage.getItem(legacyKey(email));
    if (legacy) {
      const messages = JSON.parse(legacy) as ChatMessage[];
      if (messages.length > 0) {
        const firstUser = messages.find((m) => m.role === "user");
        const title = firstUser
          ? firstUser.content.slice(0, 40) + (firstUser.content.length > 40 ? "…" : "")
          : "Previous chat";
        const first = messages[0];
        const last = messages[messages.length - 1];
        const session: ChatSession = {
          id: String(first?.timestamp ?? Date.now()),
          title,
          createdAt: first?.timestamp ?? Date.now(),
          updatedAt: last?.timestamp ?? Date.now(),
          messages,
        };
        const sessions = [session];
        localStorage.setItem(sessionsKey(email), JSON.stringify(sessions));
        localStorage.removeItem(legacyKey(email));
        return sessions;
      }
      localStorage.removeItem(legacyKey(email));
    }
  } catch {
    // ignore
  }
  return [];
}

function saveAllSessions(email: string, sessions: ChatSession[]) {
  localStorage.setItem(sessionsKey(email), JSON.stringify(sessions.slice(0, MAX_SESSIONS)));
}

function sessionTitle(messages: ChatMessage[]): string {
  const first = messages.find((m) => m.role === "user");
  if (!first) return "New chat";
  return first.content.length > 40 ? first.content.slice(0, 40) + "…" : first.content;
}

function formatSessionDate(timestamp: number): string {
  const now = Date.now();
  const days = Math.floor((now - timestamp) / (1000 * 60 * 60 * 24));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return formatInstant(timestamp, { year: "numeric", month: "numeric", day: "numeric" });
}

export default function ChatInterface({
  currentNoteId,
  currentNoteTitle,
  userEmail,
  userName,
  activeWorkspaceId,
  onThinkingChange,
  newChatVersion,
  showHistory,
  onHistoryClose,
}: ChatInterfaceProps) {
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => String(Date.now()));
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [allSessions, setAllSessions] = useState<ChatSession[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Persists saveIdAs IDs across turns so follow-up prompts like "share this page" work
  const [persistentMemory, setPersistentMemory] = useState<Record<string, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load latest session when userEmail is available or changes
  useEffect(() => {
    if (!userEmail) {
      setMessages([]);
      setAllSessions([]);
      setCurrentSessionId(String(Date.now()));
      return;
    }
    const sessions = loadAllSessions(userEmail);
    setAllSessions(sessions);
    const latest = sessions[0];
    if (latest) {
      setCurrentSessionId(latest.id);
      setMessages(latest.messages);
    } else {
      setCurrentSessionId(String(Date.now()));
      setMessages([]);
    }
  }, [userEmail]);

  // Save current session whenever messages change
  useEffect(() => {
    if (!userEmail || messages.length === 0) return;
    const trimmed = messages.slice(-MAX_MESSAGES_PER_SESSION);
    const updated: ChatSession = {
      id: currentSessionId,
      title: sessionTitle(trimmed),
      createdAt: trimmed[0]?.timestamp ?? Date.now(),
      updatedAt: trimmed[trimmed.length - 1]?.timestamp ?? Date.now(),
      messages: trimmed,
    };
    const existingIndex = allSessions.findIndex((s) => s.id === currentSessionId);
    const next = existingIndex >= 0
      ? allSessions.map((s) => s.id === currentSessionId ? updated : s)
      : [updated, ...allSessions].slice(0, MAX_SESSIONS);
    saveAllSessions(userEmail, next);
    setAllSessions(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Notify parent when isLoading changes
  useEffect(() => {
    if (onThinkingChange) onThinkingChange(isLoading);
  }, [isLoading, onThinkingChange]);

  // Start new chat when newChatVersion increments
  const prevNewChatVersion = useRef(newChatVersion ?? 0);
  useEffect(() => {
    if (newChatVersion === undefined || newChatVersion === prevNewChatVersion.current) return;
    prevNewChatVersion.current = newChatVersion;
    setCurrentSessionId(String(Date.now()));
    setMessages([]);
    setPersistentMemory({});
  }, [newChatVersion]);

  // Refresh allSessions when history panel opens
  useEffect(() => {
    if (showHistory && userEmail) {
      setAllSessions(loadAllSessions(userEmail));
    }
  }, [showHistory, userEmail]);

  const handleLoadSession = (session: ChatSession) => {
    setCurrentSessionId(session.id);
    setMessages(session.messages);
    onHistoryClose?.();
  };

  const handleDeleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userEmail) return;
    const next = allSessions.filter((s) => s.id !== sessionId);
    saveAllSessions(userEmail, next);
    setAllSessions(next);
    if (sessionId === currentSessionId) {
      const fallback = next[0];
      if (fallback) {
        setCurrentSessionId(fallback.id);
        setMessages(fallback.messages);
      } else {
        setCurrentSessionId(String(Date.now()));
        setMessages([]);
      }
    }
  };

  const handleSubmit = async (e?: React.FormEvent, overrideText?: string) => {
    e?.preventDefault();
    const text = overrideText ?? inputText;
    if (!text.trim()) return;

    const userMessage: ChatMessage = { role: "user", content: text, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setInputText("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    // Build history for the AI: all messages so far + the new user message
    const historyForAgent = [
      ...messages.map(m => ({ role: m.role, content: m.content })),
      { role: "user" as const, content: text },
    ];

    try {
      const result = await postWithAuth("/api/agent", {
        prompt: text,
        activeWorkspaceId: activeWorkspaceId ?? undefined,
        currentNoteId,
        conversationHistory: historyForAgent,
        persistedMemory: persistentMemory,
      });

      const assistantContent = result.success && result.response
        ? result.response
        : result.error || "Sorry, I encountered an error processing your request.";

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assistantContent, timestamp: Date.now() },
      ]);

      // Persist any new saveIdAs IDs so follow-up prompts can reference them
      if (result.success && result.memory) {
        const SYSTEM_KEYS = new Set([
          "workspaceId", "rootBlockId", "__members__", "__workareas__",
          "__userEmail__", "__userId__", "__currentPageId__",
        ]);
        const newIds = Object.fromEntries(
          Object.entries(result.memory as Record<string, string>).filter(
            ([k]) => !SYSTEM_KEYS.has(k) && !k.startsWith("workarea_")
          )
        );
        if (Object.keys(newIds).length > 0) {
          setPersistentMemory(prev => ({ ...prev, ...newIds }));
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I encountered an error processing your request.", timestamp: Date.now() },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const hasMessages = messages.length > 0;
  const isAtLimit = messages.length >= MAX_MESSAGES_PER_SESSION;
  const displayName = userName?.split(" ")[0] || "there";

  // History panel
  if (showHistory) {
    return (
      <div className="flex flex-col h-full bg-background dark:bg-[#2c2c2c]">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
          <span className="text-[13px] font-semibold text-gray-900 dark:text-white">Recent chats</span>
          <button
            type="button"
            onClick={onHistoryClose}
            className="p-1 rounded hover-component  transition-colors text-gray-400 dark:text-gray-500"
            aria-label="Close history"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto clean-scrollbar">
          {allSessions.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-[12px] text-gray-400 dark:text-gray-500">No past conversations</p>
            </div>
          ) : (
            <div className="py-1">
              {allSessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => handleLoadSession(session)}
                  className={`w-full text-left px-4 py-2.5 flex items-start justify-between gap-2 hover-component  transition-colors group ${
                    session.id === currentSessionId ? "bg-selected " : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-[13px] truncate ${
                        session.id === currentSessionId
                          ? "font-medium text-gray-900 dark:text-white"
                          : "text-gray-700 dark:text-gray-300"
                      }`}>
                        {session.title}
                      </p>
                      {session.id === currentSessionId && (
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-blue-500" />
                      )}
                    </div>
                    {/* <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                      {formatSessionDate(session.updatedAt)}
                    </p> */}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteSession(session.id, e)}
                    className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 text-gray-300 dark:text-gray-600 hover:text-red-400 dark:hover:text-red-400 transition-all"
                    aria-label="Delete conversation"
                  >
                    <Trash2 size={13} />
                  </button>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background dark:bg-[#2c2c2c] overflow-hidden text-[14px]">
      {/* Messages area or empty state */}
      <div className="flex-1 overflow-y-auto clean-scrollbar">
        {!hasMessages ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full px-6 py-8 text-center">
            <Image src="/Chatboat Logo.svg" alt="Assistant" width={52} height={52} className="mb-4" />

            <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white mb-1">
              How can I help, {displayName}?
            </h2>
            <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-5">
              Ask me anything about your notes
            </p>

            <div className="flex flex-wrap gap-2 justify-center">
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => handleSubmit(undefined, action.prompt)}
                  className="px-3 py-1.5 text-[12px] rounded-full border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover-component  hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Messages list */
          <div className="p-4 space-y-3">
            {messages.map((message, i) => (
              <div
                key={`${message.role}-${i}-${message.content.substring(0, 10)}`}
                className={`flex gap-2 items-start ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[82%] ${message.role === "user" ? "order-first" : ""}`}>
                  {message.role === "user" ? (
                    <div className="bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 rounded-2xl rounded-tr-sm text-[13px] leading-relaxed">
                      {message.content}
                    </div>
                  ) : (
                    <div className="text-gray-800 dark:text-gray-200 text-[13px] leading-relaxed whitespace-pre-wrap">
                      {message.content}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Thinking indicator */}
            {isLoading && (
              <div className="flex items-center gap-1 pl-1 pt-1">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="px-3 pt-3 pb-2">
        {isAtLimit ? (
          <div className="text-center py-2.5 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-[12px] text-gray-500 dark:text-gray-400">
              Conversation limit reached.{" "}
              <span className="text-blue-500 dark:text-blue-500">Start a new chat</span>{" "}
              to continue.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-background dark:bg-[#2c2c2c]">
              <textarea
                ref={textareaRef}
                rows={1}
                className="w-full px-3 pt-2.5 pb-1 text-[13px] bg-white dark:bg-background resize-none focus:outline-none text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 clean-scrollbar"
                placeholder="Ask about your notes..."
                value={inputText}
                onChange={(e) => {
                  setInputText(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e as unknown as React.FormEvent);
                  }
                }}
                disabled={isLoading}
                style={{ minHeight: 36, maxHeight: 140, overflowY: "auto" }}
              />

              <div className="flex items-center justify-between px-2 pb-2 pt-0.5">
                <div className="flex items-center gap-1">
                  {/* {hasMessages && (
                    <span className="text-[10px] text-gray-300 dark:text-gray-600">
                      {messages.length}/{MAX_MESSAGES_PER_SESSION}
                    </span>
                  )} */}
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !inputText.trim()}
                  className="flex items-center justify-center w-7 h-7 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed text-white transition-opacity bg-black dark:bg-white dark:text-black"
                  aria-label="Send message"
                  title="Send message"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </form>
        )}

        <p className="text-center text-[10px] text-gray-400 dark:text-gray-600 mt-1.5">
          Uses AI · Verify important results
        </p>
      </div>
    </div>
  );
}
